import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, DatabaseZap, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { runProfileDeduplication, type DedupeResult } from "@/lib/admin-dedupe.functions";

export const Route = createFileRoute("/admin/settings")({
  head: () => ({
    meta: [
      { title: "Developer Tools - WeLivdIt Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminSettingsPage,
});

function AdminSettingsPage() {
  const navigate = useNavigate();
  const [gate, setGate] = useState<"loading" | "ok" | "denied">("loading");
  const [scanning, setScanning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [preview, setPreview] = useState<DedupeResult | null>(null);

  const dedupeFn = useServerFn(runProfileDeduplication);

  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin/settings" } });
        return;
      }
      setGate((await hasAdminAccess(sess.session)) ? "ok" : "denied");
    })();
  }, [navigate]);

  const runDryRun = async () => {
    setScanning(true);
    try {
      setPreview(await dedupeFn({ data: { dryRun: true } }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Dry run failed");
    } finally {
      setScanning(false);
    }
  };

  const execute = async () => {
    setExecuting(true);
    try {
      const res = await dedupeFn({ data: { dryRun: false } });
      toast.success(
        `Cleanup complete · ${res.purged} duplicate row${res.purged === 1 ? "" : "s"} purged · ${res.relinked} record${res.relinked === 1 ? "" : "s"} relinked`,
      );
      setPreview(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Cleanup failed");
    } finally {
      setExecuting(false);
    }
  };

  if (gate === "denied")
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <p className="text-muted-foreground">Not authorized.</p>
      </div>
    );

  return (
    <AdminShell
      title="Developer Tools"
      description="Maintenance utilities for the student database. Every destructive action runs a dry run first."
    >
      <div className="mt-6 max-w-3xl rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <DatabaseZap className="h-5 w-5" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold">Database Deduplication</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Groups student records by normalized email and phone, keeps the most recent record in each group,
              relinks dependent rows (payments, intakes, approvals) to it, and purges the leftovers.
            </p>
            <Button onClick={runDryRun} disabled={scanning || gate !== "ok"} className="mt-4 gap-2">
              {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseZap className="h-4 w-4" />}
              Run Database Deduplication
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Dry run results</DialogTitle>
            <DialogDescription>
              Nothing has been changed yet. Review the impact below before committing.
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Duplicate groups" value={preview.totalGroups} />
                <Stat label="Rows to purge" value={preview.totalPurgeable} />
              </div>

              {preview.totalGroups === 0 ? (
                <p className="rounded-xl bg-muted/50 p-3 text-muted-foreground">
                  No duplicates found — the database is already clean.
                </p>
              ) : (
                <div className="max-h-64 space-y-2 overflow-y-auto rounded-xl border border-border p-3">
                  {preview.groups.map((g) => (
                    <div key={`${g.table}-${g.keepId}`} className="text-xs">
                      <p className="font-semibold">
                        {g.identity}{" "}
                        <span className="font-normal text-muted-foreground">({g.table})</span>
                      </p>
                      <p className="text-muted-foreground">
                        {g.skipped
                          ? `Skipped — ${g.reason}`
                          : `Keeps 1 record · purges ${g.purgeIds.length}`}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {preview.skippedGroups > 0 && (
                <p className="inline-flex items-start gap-2 rounded-xl bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                  <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {preview.skippedGroups} group(s) involve separate sign-in accounts and are reported only — they
                  will not be deleted.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} disabled={executing}>
              Cancel
            </Button>
            <Button
              onClick={execute}
              disabled={executing || !preview || preview.totalPurgeable === 0}
              className="gap-2"
            >
              {executing && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirm &amp; Execute Cleanup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-display text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}
